import React, { useEffect } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Common, SettingsIcons } from "../../assets/images";
// Redux imports
import { Billing } from "../../interfaces/account-charts";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setFilterBillTotalPages,
  setBillListLoading,
} from "../../store/slices/vendorsSlice";
// API imports
import { useGetFilterBillingListMutation } from "../../services/AccountCharts";
import SelectSearch from "react-select-search";

interface Props {
  show?: boolean;
  setShow?: any;
  setBills?: any;
  filterSearch: boolean;
  setFilterSearch: any;
  bills: Billing[];
  query: string;
  filterInput: FormProps;
  setFilterInput: any;
}

export interface FormProps {
  approvalStatus: string;
  billStatus: string;
  billFrom: string;
  billTo: string;
  dueDate: string;
}

const BillingFilterModal = ({
  show,
  setShow,
  bills,
  setBills,
  setFilterSearch,
  query,
  filterInput,
  setFilterInput,
  filterSearch,
}: Props) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.vendors);
  const accessToken = useAppSelector((state) => state.common.accessToken);
  const userID = useAppSelector((state) => state.common.userID);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const [getFilterBillingList] = useGetFilterBillingListMutation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(setBillListLoading(true));
    setFilterSearch(true);
    setShow(false);
    // Filter employees based on the form values
    try {
      const data = {
        userID: userID || "",
        accessToken: accessToken || "",
        permission: profileData?.permissions.showPettyCash || false,
        payload: {
          page: state.filterBillPageNo,
          limit: state.filterBillPageSize,
          approvalStatus: filterInput.approvalStatus,
          billStatus: filterInput.billStatus,
          billFrom: filterInput.billFrom,
          billTo: filterInput.billTo,
          dueDate: filterInput.dueDate,
        },
      };
      const response = await getFilterBillingList(data).unwrap();
      if (response) {
        setBills(response.data.bills[0].data);
        dispatch(
          setFilterBillTotalPages(response.data.bills[0].metadata[0].total),
        );
      }
    } catch (e: any) {
    } finally {
      dispatch(setBillListLoading(false));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      dispatch(setBillListLoading(true));
      // Filter employees based on the form values
      try {
        const data = {
          userID: userID || "",
          accessToken: accessToken || "",
          permission: profileData?.permissions.showEmployee || false,
          payload: {
            page: state.filterBillPageNo,
            limit: state.filterBillPageSize,
            billNo: query,
            approvalStatus: filterInput.approvalStatus,
            billStatus: filterInput.billStatus,
            billFrom: filterInput.billFrom,
            billTo: filterInput.billTo,
            dueDate: filterInput.dueDate,
          },
        };
        const response = await getFilterBillingList(data).unwrap();
        if (response) {
          setBills(response.data.bills[0].data);
          dispatch(
            setFilterBillTotalPages(response.data.bills[0].metadata[0].total),
          );
        }
      } catch (e: any) {
      } finally {
        dispatch(setBillListLoading(false));
      }
    };
    if (filterSearch) {
      fetchData();
    }
  }, [state.filterBillPageNo, state.filterBillPageSize, query]);

  const handleReset = () => {
    setBills(bills);
    setFilterInput({
      approvalStatus: "",
      billStatus: "",
      billFrom: "",
      billTo: "",
      dueDate: "",
    });
  };

  return (
    <>
      <Modal
        show={show}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="lg"
        className="general-modal"
      >
        <Modal.Body className="text-center px-5">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="heading">Filter Bills</h4>
            <button className="btn p-0 border-0" onClick={() => setShow(false)}>
              <img src={SettingsIcons.CrossIcon} alt="edit-icon" />
            </button>
          </div>
          <div className="change-password">
            <Form onSubmit={handleSubmit} className="text-end">
              <Row>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Approval Status
                    </h4>
                    <Form.Select
                      name="approvalStatus"
                      onChange={(e) =>
                        setFilterInput({
                          ...filterInput,
                          approvalStatus: e.target.value,
                        })
                      }
                      value={filterInput.approvalStatus}
                      className="custom-input border-fill"
                    >
                      <option value="">Select Approval Status</option>
                      <option value="Approvals Pending">
                        Approvals Pending
                      </option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Bill Status
                    </h4>
                    <Form.Select
                      name="billStatus"
                      onChange={(e) =>
                        setFilterInput({
                          ...filterInput,
                          billStatus: e.target.value,
                        })
                      }
                      value={filterInput.billStatus}
                      className="custom-input border-fill"
                    >
                      <option value="">Select Bill Status</option>
                      <option value="Paid">Paid</option>
                      <option value="Un paid">Un paid</option>
                      <option value="Hold">Un paid</option>
                      <option value="Cancelled">Cancelled</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Bill From
                    </h4>
                    <Form.Control
                      type="date"
                      name="billFrom"
                      placeholder="Enter bill from"
                      onChange={(e) =>
                        setFilterInput({
                          ...filterInput,
                          billFrom: e.target.value,
                        })
                      }
                      value={filterInput.billFrom}
                      className="custom-input border-fill"
                    />
                  </Form.Group>
                </Col>
                <Col sm="6">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Bill To
                    </h4>
                    <Form.Control
                      type="date"
                      name="billTo"
                      placeholder="Enter bill to"
                      onChange={(e) =>
                        setFilterInput({
                          ...filterInput,
                          billTo: e.target.value,
                        })
                      }
                      value={filterInput.billTo}
                      className="custom-input border-fill"
                    />
                  </Form.Group>
                </Col>
                <Col sm="12">
                  <Form.Group
                    className="mb-3 text-start"
                    controlId="formBasicEmail"
                  >
                    <h4
                      className="sub-heading normal-font-weight textBlack"
                      style={{ fontSize: "18px" }}
                    >
                      Due Date
                    </h4>
                    <Form.Control
                      type="date"
                      name="dueDate"
                      placeholder="Enter due date"
                      onChange={(e) =>
                        setFilterInput({
                          ...filterInput,
                          dueDate: e.target.value,
                        })
                      }
                      value={filterInput.dueDate}
                      className="custom-input border-fill"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="mt-4">
                <button
                  className="btn primary-button text-center w-25 me-2"
                  onClick={handleReset}
                  type="button"
                >
                  Reset
                </button>
                <button
                  className="btn primary-button text-center w-25"
                  type="submit"
                >
                  Filter
                </button>
              </div>
            </Form>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BillingFilterModal;
